import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { CreateDrawDealsAlert, DismissDrawDealsAlert, GetRenewalRingResults, RingDeals } from './draw-deal.actions';
import { Alert } from 'app/models/const/alert.model';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { DrawDealService } from 'app/services/draw-deal/draw-deal.service';

export interface RenewalRingResults {
  numAccepted: number;
  numRejected: number;
  failedDeals: any[];
}

export class DrawDealStateModel {
  results: RenewalRingResults | null;
  loading: boolean;
  alerts: Alert[];
}

@State<DrawDealStateModel>({
  name: 'drawDeals',
  defaults: {
    results: null,
    loading: false,
    alerts: []
  }
})
@Injectable()

export class DrawDealState {

  @Selector()
  static results(state: DrawDealStateModel) {
    return state.results;
  }

  @Selector()
  static loading(state: DrawDealStateModel) {
    return state.loading;
  }

  @Selector()
  static alerts(state: DrawDealStateModel) {
    return state.alerts;
  }

  constructor(
    private drawDealService: DrawDealService,
    private pusherService: PusherService,
    private store: Store
  ) { }

  @Action(RingDeals)
  ringDeals({ patchState }: StateContext<DrawDealStateModel>, { file }: RingDeals) {
    patchState({ loading: true });
    return this.drawDealService.ring(file).pipe(
      catchError(err => {
        this.store.dispatch(new CreateDrawDealsAlert(
          'Your file could not be uploaded. Please try again later.',
          'error'
        ));
        throw err;
      }),
      tap((res: any) => {
        this.pusherSubscribe(JSON.parse(res)?.renewalLogId);
        this.store.dispatch(new CreateDrawDealsAlert(
          'Your file has been uploaded and queued for processing. Processing may take several minutes.',
          'info'
        ));
      })
    );
  }

  @Action(GetRenewalRingResults)
  getRenewalRingResults({ patchState }: StateContext<DrawDealStateModel>, { renewalLogId }: GetRenewalRingResults) {
    return this.drawDealService.getResults(renewalLogId).pipe(
      catchError(err => {
        this.store.dispatch(new CreateDrawDealsAlert(
          // eslint-disable-next-line max-len
          'Your file was processed but there was an issue fetching the results. Try uploading the file again or contact the Pipeline development team.',
          'error'
        ));
        throw err;
      }),
      tap((res: any) => {
        patchState({
          results: {
            numAccepted: res.numAccepted,
            numRejected: res.numRejected,
            failedDeals: JSON.parse(res.rejectedDealsJson).filter(r => r.borrowerId)
          },
          loading: false
        });
        this.pusherUnsubscribe(renewalLogId);
        this.store.dispatch(new CreateDrawDealsAlert(
          'Your file was successfully processed. Please view the results below.',
          'success'
        ));
      })
    );
  }

  @Action(CreateDrawDealsAlert)
  CreateDrawDealsAlert({ getState, patchState }: StateContext<DrawDealStateModel>, { message, level }: CreateDrawDealsAlert) {
    patchState({ alerts: [ ...getState().alerts, { level, dismissDelay: 5000, message } ]});
  }

  @Action(DismissDrawDealsAlert)
  dismissDrawDealsAlert({ getState, patchState }: StateContext<DrawDealStateModel>, { alert }: DismissDrawDealsAlert) {
    const alertsCopy = [ ...getState().alerts ];
    const updatedAlerts = alertsCopy.filter(a => a.level !== alert.level && a.message !== alert.message);
    patchState({ alerts: updatedAlerts });
  }

  private pusherSubscribe(renewalLogId: number) {
    this.pusherService
      .subscribe(`ring-draw-deals-${renewalLogId}`)
      .bind_global((eventName: string) => {
        if (eventName === 'complete') {
          this.store.dispatch(new GetRenewalRingResults(renewalLogId));
        }
    });
  }

  private pusherUnsubscribe(renewalLogId: number) {
    this.pusherService.unsubscribe(`ring-draw-deals-${renewalLogId}`);
  }
}
